<template>
  <div
    ref="container"
    class="page-auth"
    data-cy="page:login"
  >
    <transition
      v-if="isOnScreen(SCREEN.LOGIN)"
      appear
      appear-active-class="animate__animated animate__fadeIn ease-in-out a3"
    >
      <auth-login
        class="page-auth__form"
        :is-pending="isPending"
        :callback="loginCallback"
        :has-remote-error="hasRemoteError"
      />
    </transition>

    <transition
      v-if="isOnScreen(SCREEN.MFA)"
      appear
      appear-active-class="animate__animated animate__fadeIn ease-in-out a3"
    >
      <mfa-form
        ref="mfaForm"
        :is-pending="isPending"
        :submit-callback="onSubmitMfa"
        :has-remote-error="hasRemoteError"
        :remote-error-message="remoteErrorMessage"
        focus
      />
    </transition>
  </div>
</template>

<style lang="sass" scoped>

/// Animation
///
/// On login success, animate the whole page as if we were transporting
/// within a cloud
.page-auth--success
  transition: opacity .4s ease-in-out
  opacity: 0

/// Animation
///
/// On login error, animate the error caption (handy when the authentication
/// failed twice and thus give a user feedback of the error)
.page-auth
  :deep(.login-form__error)
    visibility: hidden
.page-auth.page-auth--error
  :deep(.login-form__error)
    visibility: visible
    animation: headShake 1s ease-in-out 0.2s, fade-in 0.4s ease-in-out

</style>

<script lang="ts">
import AuthLogin from '~/components/account/authentication/LoginForm.vue'
import Console from '~/utils/lib/core/console'
import { delay, equals, sustainedPromise, toString } from '~/utils/lib/core/prelude'
import { EVENTS as ACCOUNT_EVENTS, MICRO_SERVICE_ERRORS as ACCOUNT_MICRO_SERVICE_ERRORS, observer as accountObserver } from '~/utils/infra/account/types'
import AccountAPIError from '~/utils/infra/account/error'
import { cause } from '~/utils/infra/common/helpers'
import MfaForm from '~/components/account/authentication/MfaForm.vue'
import { useToastBuilder } from '~/composables/layout/toast'
import { TRY_LOGIN_SUCCESS_TYPE, useStore_account_authentication } from '~/stores/account/authentication'
import { useLinkHandler } from '~/composables/core/link-handler'

const console = new Console('component:authentication')

type SCREEN = typeof SCREEN[keyof typeof SCREEN]
const SCREEN = {
  LOGIN: 'login',
  MFA: 'mfa'
} as const

export default defineNuxtComponent({
  components: { AuthLogin, MfaForm },
  setup() {
    definePageMeta({
      layout: 'authentication',
      pageTransition: {
        enterActiveClass: 'animate__animated animate__fadeIn ease-in-out a3',
        leaveActiveClass: 'd-none',
      },
      middleware: [
        'anonymous-control-gate',
      ]
    })
    useHead({
      title: 'Connexion — Hamac',
    })

    const ToastBuilder = useToastBuilder()

    const refContainer = useTemplateRef<HTMLDivElement>('container')

    const store_authentication = useStore_account_authentication()

    return {
      ...useLinkHandler(),
      ToastBuilder,
      refContainer,
      store_authentication,
    }
  },
  data() {
    return {
      SCREEN,
      isPending: false,
      hasRemoteError: false,
      remoteErrorMessage: '', // mutates
      withRememberMe: false, // mutates
      rememberMe: true,
      currentScreen: SCREEN.LOGIN as SCREEN,
    }
  },
  methods: {
    onSubmitMfa(code) {
      this.isPending = true
      this.hasRemoteError = false

      return sustainedPromise(
          500, // Delay to prevent flashy animations
          this.store_authentication.submitCode(
            {
              code,
              rememberMe: this.withRememberMe
            }
          ),
        )
        .then(() => this.onLoginSuccess())
        .catch(err => this.onLoginError(err))
    },
    loginCallback(data) {
      this.isPending = true
      this.withRememberMe = data.rememberMe

      this.refContainer
        ?.classList
        .remove('page-auth--error')

      return this.store_authentication.login_v2(data)
        .then(res => {
          switch (res) {

            case TRY_LOGIN_SUCCESS_TYPE.MFA_CHALLENGE:
              this.hasRemoteError = false
              this.currentScreen = SCREEN.MFA
              this.isPending = false
              break

            case TRY_LOGIN_SUCCESS_TYPE.COMPLETE:
              return this.onLoginSuccess()

          }
        })
        .catch(err => this.onLoginError(err))
    },
    onLoginError(err) {

      switch (true) {

        case
          (  err instanceof AccountAPIError
          && cause(err) === ACCOUNT_MICRO_SERVICE_ERRORS.LOGIN_INCORRECT_PASSWORD
          ):
        case
          (  err instanceof AccountAPIError
          && cause(err) === ACCOUNT_MICRO_SERVICE_ERRORS.LOGIN_ACCOUNT_NOT_FOUND
          ):
            // as we are dealing a signup form, this must be an invalid
            // credentials → silently discard the error
            break

        default:

          console.warn('login error', toString(err), err)

          break
      }

      this.hasRemoteError = true
      this.remoteErrorMessage = toString(err)
      this.isPending = false

      // animate server error caption
      this.refContainer
        ?.classList
        .add('page-auth--error')
    },
    async onLoginSuccess() {

      this.refContainer
        ?.classList
        .add('page-auth--success')

      window.document.querySelector( '.layout-authentication' )
        ?.classList
        .add('layout-authentication--success')

      // wait for animation to finish off
      await delay(400)

      return accountObserver.emit(ACCOUNT_EVENTS.ON_SIGNIN)
    },
    handleRememberMeChange(val) {
      this.rememberMe = val
    },
    isOnScreen(screen :SCREEN) {
      return equals(screen, this.currentScreen)
    }
  },
})
</script>
